const TOKEN_KEY = "mizu-admin-token";
const USERNAME_KEY = "mizu-admin-username";

function getApiBase() {
  if (typeof window === "undefined") return "";

  const { hostname, port } = window.location;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    if (port === "3006") {
      return "http://localhost:8006";
    }
  }

  return "";
}

export function getAdminToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setAdminToken(token) {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function getAdminUsername() {
  return sessionStorage.getItem(USERNAME_KEY);
}

export function setAdminUsername(username) {
  sessionStorage.setItem(USERNAME_KEY, username);
}

export function clearAdminToken() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USERNAME_KEY);
}

export async function subscribeToNewsletter(email, lang) {
  const response = await fetch(`${getApiBase()}/api/subscribe`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, lang }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "subscribe_failed");
  }

  return response.json();
}

export async function adminLogin(username, password) {
  const response = await fetch(`${getApiBase()}/api/admin/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ username, password }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "login_failed");
  }

  return data;
}

export async function fetchSubscribers(token) {
  const response = await fetch(`${getApiBase()}/api/admin/subscribers`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "fetch_failed");
  }

  return data;
}

export async function deleteSubscriber(token, email) {
  const response = await fetch(
    `${getApiBase()}/api/admin/subscribers/${encodeURIComponent(email)}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "delete_failed");
  }

  return data;
}

export async function updateSubscriberStatus(token, email, updates) {
  const response = await fetch(
    `${getApiBase()}/api/admin/subscribers/${encodeURIComponent(email)}/status`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    },
  );

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "update_failed");
  }

  return data;
}

export async function importSubscribers(token, payload) {
  const response = await fetch(`${getApiBase()}/api/admin/subscribers/import`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "import_failed");
  }

  return data;
}

async function adminEmailFetch(token, path, options = {}) {
  const response = await fetch(`${getApiBase()}/api/admin/email${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "request_failed");
  }

  return data;
}

export async function fetchEmailCatalog(token) {
  return adminEmailFetch(token, "/test-catalog");
}

export async function fetchEmailPreview(token, payload) {
  return adminEmailFetch(token, "/preview", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function sendTestEmail(token, payload) {
  return adminEmailFetch(token, "/send-test", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function sendSubscriberEmail(token, payload) {
  return adminEmailFetch(token, "/send-to-subscriber", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchEmailRecipientCount(token, langFilter = "all") {
  const params = new URLSearchParams({ langFilter });
  return adminEmailFetch(token, `/recipient-count?${params}`);
}

export async function sendEmailCampaign(token, payload) {
  return adminEmailFetch(token, "/send", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchEmailCampaigns(token) {
  return adminEmailFetch(token, "/campaigns");
}

export async function fetchSiteMode() {
  const response = await fetch(`${getApiBase()}/api/site-mode`, {
    cache: "no-store",
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "site_mode_failed");
  }

  return data;
}

export async function fetchAdminSiteMode(token) {
  const response = await fetch(`${getApiBase()}/api/admin/settings/site-mode`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "settings_read_failed");
  }

  return data;
}

export async function updateAdminSiteMode(token, showComingSoon, updatedBy = "") {
  const response = await fetch(`${getApiBase()}/api/admin/settings/site-mode`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ showComingSoon, updatedBy }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "settings_update_failed");
  }

  return data;
}

export async function fetchShopSettings() {
  const response = await fetch(`${getApiBase()}/api/shop`, {
    cache: "no-store",
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "shop_read_failed");
  }

  return data;
}

export async function fetchStripeConfig() {
  const response = await fetch(`${getApiBase()}/api/stripe/config`, {
    cache: "no-store",
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "stripe_config_failed");
  }

  return data;
}

export async function createCoffeePaymentIntent(payload) {
  const response = await fetch(`${getApiBase()}/api/stripe/create-payment-intent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "payment_create_failed");
  }

  return data;
}

export async function completeCoffeeOrder(paymentIntentId) {
  const response = await fetch(`${getApiBase()}/api/stripe/complete-order`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ paymentIntentId }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "order_complete_failed");
  }

  return data;
}

export async function fetchAdminShopSettings(token) {
  const response = await fetch(`${getApiBase()}/api/admin/settings/shop`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "shop_read_failed");
  }

  return data;
}

export async function updateAdminShopSettings(token, payload) {
  const response = await fetch(`${getApiBase()}/api/admin/settings/shop`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "shop_update_failed");
  }

  return data;
}

export async function fetchAdminOrders(token, { status = "", q = "", limit = 50, skip = 0 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (q) params.set("q", q);
  params.set("limit", String(limit));
  params.set("skip", String(skip));

  const response = await fetch(`${getApiBase()}/api/admin/orders?${params}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "orders_list_failed");
  }

  return data;
}

export async function updateAdminOrderStatus(token, orderId, status) {
  const response = await fetch(`${getApiBase()}/api/admin/orders/${orderId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "order_update_failed");
  }

  return data;
}

export async function deleteAdminOrder(token, orderId) {
  const response = await fetch(`${getApiBase()}/api/admin/orders/${orderId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "order_delete_failed");
  }

  return data;
}

export async function createBooking(payload) {
  const response = await fetch(`${getApiBase()}/api/bookings`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "booking_failed");
  }

  return data;
}

export async function fetchAdminBookings(token, { status = "", q = "", limit = 50, skip = 0 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (q) params.set("q", q);
  params.set("limit", String(limit));
  params.set("skip", String(skip));

  const response = await fetch(`${getApiBase()}/api/admin/bookings?${params}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "bookings_list_failed");
  }

  return data;
}

export async function decideAdminBooking(token, bookingId, decision) {
  const response = await fetch(`${getApiBase()}/api/admin/bookings/${bookingId}/decision`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ decision }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "booking_update_failed");
  }

  return data;
}

export async function deleteAdminBooking(token, bookingId) {
  const response = await fetch(`${getApiBase()}/api/admin/bookings/${bookingId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "booking_delete_failed");
  }

  return data;
}

/** Uploaded media is served by the API; in local dev that is a different port. */
export function resolveMediaUrl(url) {
  if (typeof url === "string" && url.startsWith("/api/")) {
    return `${getApiBase()}${url}`;
  }
  return url || "";
}

async function apiRequest(path, { token, method = "GET", body, fallbackError } = {}) {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const response = await fetch(`${getApiBase()}${path}`, {
    method,
    headers,
    cache: "no-store",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.error || fallbackError);
    error.productId = data.productId;
    throw error;
  }

  return data;
}

export function fetchShopProducts() {
  return apiRequest("/api/shop/products", { fallbackError: "products_read_failed" });
}

export function createCartPaymentIntent(payload) {
  return apiRequest("/api/stripe/create-cart-payment-intent", {
    method: "POST",
    body: payload,
    fallbackError: "payment_create_failed",
  });
}

export function fetchAdminProducts(token) {
  return apiRequest("/api/admin/products", { token, fallbackError: "products_list_failed" });
}

export function createAdminProduct(token, payload) {
  return apiRequest("/api/admin/products", {
    token,
    method: "POST",
    body: payload,
    fallbackError: "product_create_failed",
  });
}

export function updateAdminProduct(token, productId, payload) {
  return apiRequest(`/api/admin/products/${productId}`, {
    token,
    method: "PATCH",
    body: payload,
    fallbackError: "product_update_failed",
  });
}

export function uploadAdminProductImage(token, productId, image) {
  return apiRequest(`/api/admin/products/${productId}/image`, {
    token,
    method: "PUT",
    body: { image },
    fallbackError: "image_upload_failed",
  });
}

export function deleteAdminProduct(token, productId) {
  return apiRequest(`/api/admin/products/${productId}`, {
    token,
    method: "DELETE",
    fallbackError: "product_delete_failed",
  });
}
